import { useState, useMemo } from "react";
import type { CampaignSummary, TreeCreateResult } from "@/types/campaign";
import {
  CAMPAIGN_TYPE_LABELS,
  CAMPAIGN_STATUS_LABELS,
} from "@/types/campaign";
import { useCampaignsList, useProjectsList } from "@/hooks";
import PageHeader from "@/components/shared/PageHeader";
import CampaignCard from "./components/CampaignCard";
import CampaignDetailDrawer from "./components/CampaignDetailDrawer";
import QuickTreeForm from "./components/QuickTreeForm";

export default function CampanhasPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignSummary | null>(null);
  const [showQuickTree, setShowQuickTree] = useState(false);
  const [quickTreeCampaign, setQuickTreeCampaign] = useState<CampaignSummary | null>(null);

  const { campaigns, loading, refetch } = useCampaignsList();
  const { items: projects } = useProjectsList();

  const filtered = useMemo(() => {
    let result = [...campaigns];
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          c.projectName.toLowerCase().includes(q) ||
          (c.areaPilotoName ?? "").toLowerCase().includes(q)
      );
    }
    if (statusFilter) result = result.filter((c) => c.status === statusFilter);
    if (typeFilter) result = result.filter((c) => c.campaignType === typeFilter);
    if (projectFilter) result = result.filter((c) => c.projectName === projectFilter);
    return result;
  }, [campaigns, search, statusFilter, typeFilter, projectFilter]);

  const kpis = useMemo(() => {
    const total = campaigns.length;
    const active = campaigns.filter((c) => c.status === "EM_ANDAMENTO").length;
    const concluded = campaigns.filter((c) => c.status === "CONCLUIDA").length;
    const trees = campaigns.reduce((s, c) => s + c.registeredTreeCount, 0);
    return { total, active, concluded, trees };
  }, [campaigns]);

  const handleSelectCampaign = (c: CampaignSummary) => setSelectedCampaign(c);

  const handleRegisterTree = () => {
    const target = selectedCampaign;
    if (target) {
      setQuickTreeCampaign(target);
      setShowQuickTree(true);
    }
  };

  const handleTreeCreated = (_result: TreeCreateResult) => {
    refetch();
  };

  const clearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setProjectFilter("");
    setSearch("");
  };

  const hasFilters = !!(statusFilter || typeFilter || projectFilter || search);

  return (
    <div>
      <PageHeader
        title="Campanhas de Campo"
        subtitle="Organize equipes, quadras e metas para inventário e monitoramento arbóreo."
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total de campanhas" value={kpis.total} icon="ri-compass-3-line" />
        <KpiCard label="Em andamento" value={kpis.active} icon="ri-run-line" color="text-green-600" />
        <KpiCard label="Concluídas" value={kpis.concluded} icon="ri-check-double-line" color="text-blue-600" />
        <KpiCard label="Árvores registradas" value={kpis.trees} icon="ri-plant-line" color="text-emerald-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            placeholder="Buscar por nome, código ou projeto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos os status</option>
          {Object.entries(CAMPAIGN_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos os tipos</option>
          {Object.entries(CAMPAIGN_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {projects.length > 0 && (
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Todos os projetos</option>
            {projects.map((p) => (
              <option key={p.id} value={p.nome}>{p.nome}</option>
            ))}
          </select>
        )}
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-foreground-500 hover:text-foreground-700 transition"
          >
            <i className="ri-close-line mr-1" />
            Limpar
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-3xl" />
          <p className="mt-2 text-sm">Carregando campanhas...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-filter-off-line text-3xl" />
          <p className="mt-2 text-sm">Nenhuma campanha encontrada com os filtros aplicados.</p>
          <button
            onClick={clearFilters}
            className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Limpar filtros
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <CampaignCard key={c.id} campaign={c} onSelect={handleSelectCampaign} />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selectedCampaign && (
        <CampaignDetailDrawer
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onRegisterTree={handleRegisterTree}
        />
      )}

      {/* FIQ-1 Quick Tree Form */}
      {showQuickTree && quickTreeCampaign && (
        <QuickTreeForm
          campaign={quickTreeCampaign}
          onClose={() => {
            setShowQuickTree(false);
            setQuickTreeCampaign(null);
          }}
          onCreated={handleTreeCreated}
        />
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  color = "text-foreground-700",
}: {
  label: string;
  value: number;
  icon: string;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <i className={`${icon} ${color}`} />
        <span className="text-xs text-foreground-500">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-20">
      <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
        <i className="ri-compass-3-line text-4xl text-emerald-400" />
      </div>
      <h3 className="text-lg font-semibold text-foreground-700 mb-1">Nenhuma campanha cadastrada</h3>
      <p className="text-sm text-foreground-400 max-w-md mx-auto">
        Campanhas de campo organizam equipes, quadras e metas para inventário arbóreo.
        Quando uma campanha for criada, ela aparecerá aqui.
      </p>
    </div>
  );
}
